import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DarkModeProvider } from './context/DarkModeContext';
import { initGA, trackPageView } from './utils/analytics';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingGhosts from './components/FloatingGhosts';
import BetaBanner from './components/BetaBanner';

// Pages
import Home from './pages/Home';
import Feed from './pages/Feed';
import Login from './pages/Login';
import Signup from './pages/Signup';
import CompleteProfile from './pages/CompleteProfile';
import Dashboard from './pages/Dashboard';
import PromptDetail from './pages/PromptDetail';
import CreatePrompt from './pages/CreatePrompt';
import EditPrompt from './pages/EditPrompt';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Leaderboard from './pages/Leaderboard';
import TokenAnalyzer from './pages/TokenAnalyzer';
import Admin from './pages/Admin';
import ClaimAutoPrompt from './pages/ClaimAutoPrompt';
import Search from './pages/Search';
import Activity from './pages/Activity';
import Collections from './pages/Collections';
import CollectionDetail from './pages/CollectionDetail';
import CreateAgent from './pages/CreateAgent';
import CreateMCPServer from './pages/CreateMCPServer';

// New Pages
import About from './pages/About';
import FAQ from './pages/FAQ';
import Documentation from './pages/Documentation';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import BetaNotice from './pages/BetaNotice';
import HowToUse from './pages/HowToUse';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, userProfile } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Only redirect to complete-profile if userProfile is explicitly null (404 error)
  // OR if userProfile exists but doesn't have a username (incomplete profile)
  if (userProfile === null || (userProfile && !userProfile.username)) {
    return <Navigate to="/complete-profile" />;
  }

  // If userProfile is undefined, we're still loading
  if (userProfile === undefined) {
    return <div className="flex justify-center items-center min-h-screen bg-white dark:bg-spooky-bg-darker">
      <div className="text-gray-500 dark:text-gray-400">Loading...</div>
    </div>;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  if (userProfile === undefined) return <div className="flex justify-center items-center min-h-screen bg-white dark:bg-spooky-bg-darker">Loading...</div>;
  const isAdmin = userProfile && ['admin','system','moderator'].includes(userProfile.role);
  if (!isAdmin) return <Navigate to="/" />;
  return children;
};

// Component to track page views (must be inside Router)
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;
    trackPageView(path, document.title);
  }, [location]);

  return null;
}

function AppContent() {
  const { currentUser } = useAuth();

  // Initialize GA4 on mount
  useEffect(() => {
    initGA();
  }, []);

  return (
    <Router>
      <PageViewTracker />
      <div className="flex flex-col min-h-screen bg-white dark:bg-spooky-bg-darker transition-colors duration-300">
        <FloatingGhosts />
        <BetaBanner />
        <Navbar />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={currentUser ? <Feed /> : <Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />

            {/* Public Info Pages */}
            <Route path="/about" element={<About />} />
            <Route path="/how-to-use" element={<HowToUse />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/docs" element={<Documentation />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/beta" element={<BetaNotice />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prompts/create"
              element={
                <ProtectedRoute>
                  <CreatePrompt />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents/create"
              element={
                <ProtectedRoute>
                  <CreateAgent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mcp-servers/create"
              element={
                <ProtectedRoute>
                  <CreateMCPServer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:username"
              element={<Profile />}
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/token-analyzer"
              element={
                <ProtectedRoute>
                  <TokenAnalyzer />
                </ProtectedRoute>
              }
            />

            {/* Public Routes */}
            <Route path="/prompts/:id" element={<PromptDetail />} />
            <Route
              path="/prompts/:id/edit"
              element={
                <ProtectedRoute>
                  <EditPrompt />
                </ProtectedRoute>
              }
            />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/search" element={<Search />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/auto-prompts/claim/:token" element={<ClaimAutoPrompt />} />
            <Route
              path="/collections"
              element={
                <ProtectedRoute>
                  <Collections />
                </ProtectedRoute>
              }
            />
            <Route path="/collections/:id" element={<CollectionDetail />} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        <Footer />
      </div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1e293b',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <DarkModeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DarkModeProvider>
    </HelmetProvider>
  );
}

export default App;
